import { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import type { 
  CustomerInfo, 
  PurchasesOfferings,
  PurchasesPackage,
  PurchasesOffering
} from '@revenuecat/purchases-capacitor';
import { initializeRevenueCat, isRevenueCatConfigured } from '@/lib/revenueCatInit';

// Entitlement identifier for pro access
export const PRO_ENTITLEMENT = 'Vent App LLC Pro';

// Product ID for therapist monthly subscription
export const THERAPIST_MONTHLY_PRODUCT = 'therapist_monthly';

export const useRevenueCat = () => {
  const [isInitialized, setIsInitialized] = useState(isRevenueCatConfigured());
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<PurchasesOffering | null>(null);
  const [isLoading, setIsLoading] = useState(!isRevenueCatConfigured());
  const [error, setError] = useState<string | null>(null);

  // Check if running on native platform
  const isNativePlatform = Capacitor.isNativePlatform();

  // Initialize RevenueCat (uses centralized init that's safe to call multiple times)
  const initialize = useCallback(async () => {
    console.log('useRevenueCat: Checking SDK status', {
      isNativePlatform,
      alreadyConfigured: isRevenueCatConfigured(),
    });

    if (!isNativePlatform) {
      console.log('useRevenueCat: Skipping - not on native platform');
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      // Use centralized initialization (safe to call multiple times)
      const success = await initializeRevenueCat();
      
      if (!success) {
        setError('Failed to initialize RevenueCat SDK');
        setIsInitialized(true); // Mark as initialized to prevent infinite loading
        setIsLoading(false);
        return;
      }
      
      console.log('useRevenueCat: SDK ready');
      setIsInitialized(true);
      setIsLoading(false);
      
      // Fetch initial customer info in background (non-blocking)
      refreshCustomerInfo().catch(console.error);
      
    } catch (err) {
      console.error('useRevenueCat: Initialization error', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize RevenueCat');
      setIsInitialized(true);
      setIsLoading(false);
    }
  }, [isNativePlatform]);

  // Refresh customer info
  const refreshCustomerInfo = useCallback(async () => {
    if (!isNativePlatform || !isInitialized) return null;

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const result = await Purchases.getCustomerInfo();
      setCustomerInfo(result.customerInfo);
      return result.customerInfo;
    } catch (err) {
      console.error('RevenueCat: Error fetching customer info', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch customer info');
      return null;
    }
  }, [isNativePlatform, isInitialized]);

  // Fetch offerings
  const fetchOfferings = useCallback(async (): Promise<PurchasesOfferings | null> => {
    if (!isNativePlatform || !isInitialized) return null;

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const result = await Purchases.getOfferings();
      
      if (result.current) {
        setOfferings(result.current);
      }
      
      return result;
    } catch (err) {
      console.error('RevenueCat: Error fetching offerings', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch offerings');
      return null;
    }
  }, [isNativePlatform, isInitialized]);

  // Check if user has pro access entitlement
  const hasProAccess = useCallback((): boolean => {
    if (!customerInfo) return false;
    return typeof customerInfo.entitlements.active[PRO_ENTITLEMENT] !== 'undefined';
  }, [customerInfo]);

  // Purchase a package
  const purchasePackage = useCallback(async (packageToPurchase: PurchasesPackage) => {
    if (!isNativePlatform || !isInitialized) {
      throw new Error('RevenueCat not available');
    }

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const result = await Purchases.purchasePackage({ aPackage: packageToPurchase });
      
      setCustomerInfo(result.customerInfo);
      return result;
    } catch (err: any) {
      // Check if user cancelled
      if (err.code === 'PURCHASE_CANCELLED') {
        console.log('RevenueCat: Purchase cancelled by user');
        return null;
      }
      
      console.error('RevenueCat: Purchase error', err);
      throw err;
    }
  }, [isNativePlatform, isInitialized]);

  // Present paywall (requires RevenueCat dashboard paywall configuration)
  const presentPaywall = useCallback(async (): Promise<boolean> => {
    console.log('RevenueCat: presentPaywall called', {
      isNativePlatform,
      isInitialized,
      hasError: Boolean(error),
      errorMessage: error,
    });

    if (!isNativePlatform) {
      console.log('RevenueCat: Skipping paywall - not on native platform');
      return false;
    }

    if (!isInitialized) {
      console.error('RevenueCat: Cannot present paywall - SDK not initialized');
      return false;
    }

    if (error) {
      console.error('RevenueCat: Cannot present paywall - initialization error:', error);
      return false;
    }

    try {
      console.log('RevenueCat: Importing RevenueCatUI module...');
      const { RevenueCatUI, PAYWALL_RESULT } = await import('@revenuecat/purchases-capacitor-ui');
      
      console.log('RevenueCat: Presenting native paywall...');
      // Present paywall with a 15-second timeout to prevent indefinite hangs
      const PAYWALL_TIMEOUT_MS = 15000;
      const paywallPromise = RevenueCatUI.presentPaywall();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Paywall presentation timed out after 15s')), PAYWALL_TIMEOUT_MS)
      );
      const { result } = await Promise.race([paywallPromise, timeoutPromise]);

      // Handle result
      switch (result) {
        case PAYWALL_RESULT.NOT_PRESENTED:
          console.log('RevenueCat: Paywall NOT_PRESENTED - check RevenueCat dashboard for paywall configuration');
          return false;
        case PAYWALL_RESULT.ERROR:
          console.error('RevenueCat: Paywall ERROR - check RevenueCat dashboard and entitlements');
          return false;
        case PAYWALL_RESULT.CANCELLED:
          console.log('RevenueCat: Paywall dismissed by user');
          return false;
        case PAYWALL_RESULT.PURCHASED:
        case PAYWALL_RESULT.RESTORED:
          console.log('RevenueCat: Purchase/restore successful', result);
          // Refresh customer info after purchase
          await refreshCustomerInfo();
          return true;
        default:
          console.log('RevenueCat: Unknown paywall result:', result);
          return false;
      }
    } catch (err) {
      console.error('RevenueCat: Paywall error', {
        error: err,
        message: err instanceof Error ? err.message : 'Unknown error',
      });
      return false;
    }
  }, [isNativePlatform, isInitialized, error, refreshCustomerInfo]);

  // Restore purchases
  const restorePurchases = useCallback(async () => {
    if (!isNativePlatform || !isInitialized) {
      throw new Error('RevenueCat not available');
    }

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const result = await Purchases.restorePurchases();
      
      setCustomerInfo(result.customerInfo);
      return result.customerInfo;
    } catch (err) {
      console.error('RevenueCat: Restore error', err);
      throw err;
    }
  }, [isNativePlatform, isInitialized]);

  // Log in with user ID (for linking purchases to your backend user)
  const logIn = useCallback(async (userId: string) => {
    if (!isNativePlatform || !isInitialized) return;

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const result = await Purchases.logIn({ appUserID: userId });
      setCustomerInfo(result.customerInfo);
      return result;
    } catch (err) {
      console.error('RevenueCat: Login error', err);
      throw err;
    }
  }, [isNativePlatform, isInitialized]);

  // Log out (reset to anonymous user)
  const logOut = useCallback(async () => {
    if (!isNativePlatform || !isInitialized) return;

    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      const result = await Purchases.logOut();
      setCustomerInfo(result.customerInfo);
      return result;
    } catch (err) {
      console.error('RevenueCat: Logout error', err);
      throw err;
    }
  }, [isNativePlatform, isInitialized]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  return {
    // State
    isInitialized,
    isLoading,
    error,
    customerInfo,
    offerings,
    isNativePlatform,
    
    // Computed
    hasProAccess: hasProAccess(),
    
    // Actions
    refreshCustomerInfo,
    fetchOfferings,
    purchasePackage,
    presentPaywall,
    restorePurchases,
    logIn,
    logOut,
  };
};

export default useRevenueCat;
